import * as _ from "../../../util/lodash";
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getContext } from 'react-recompose';
import cx from 'classnames';
import { icons, colors, positions, sizes, richTextToolbar } from "../../../enums";
import { whitelistStyles } from "../../core/hoc";
import { PrivateLabel } from "../../core/label";
import Icon from "../../core/icon";
import Tooltip from "../../core/tooltip";
import ErrorIcon from "../error-icon";
import evalPredicate from "../../../util/eval-predicate";
import QuillEditor from "./quill-editor";
/**
 * Resize handler component we are using the start the drag action
 */

var ResizeHandle = function ResizeHandle(_ref) {
  var onMouseDown = _ref.onMouseDown;
  return /*#__PURE__*/React.createElement("div", {
    className: "rich-text__editor-wrapper__drag-handle",
    onMouseDown: onMouseDown
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "20px",
    height: "20px",
    viewBox: "0 0 24 24"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M22.354 9.354l-.707-.707-13 13 .707.707zm0 7l-.707-.707-6 6 .707.707z"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M0 0h24v24H0z"
  })));
};
/**
 * Rich Text editor component built over react-quill
 */


var RichText = function RichText(p) {
  var _cx;

  var resizableDivRef = useRef();

  var _useState = useState(false),
      isResizing = _useState[0],
      setIsResizing = _useState[1];

  var _useState2 = useState(0),
      prevY = _useState2[0],
      setPrevY = _useState2[1];

  var _useState3 = useState(),
      componentHeight = _useState3[0],
      setComponentHeight = _useState3[1];

  var _useState4 = useState(false),
      hasFocus = _useState4[0],
      setHasFocus = _useState4[1];

  var handleResizeStart = function handleResizeStart(event) {
    setIsResizing(true);
    setPrevY(event.clientY);
  }; //Quill does NOT provide the event for onFocus/onBlur/onChange. Instead it's calling onFocus/onBlur with the 'range', 'source', 'editor' as parameters and calling onChange with the 'value'
  //To keep the onFocus/onBlur/onChange signature similar to the rest of them in CK, passing an empty {} instead of the event


  var handleFocus = function handleFocus() {
    setHasFocus(true);
    p.onFocus({}, {
      id: p.id,
      value: p.value
    });
  };

  var handleBlur = function handleBlur() {
    setHasFocus(false);
    p.onBlur({}, {
      id: p.id,
      value: p.value
    });
  };

  var handleChange = function handleChange(value) {
    p.onChange({}, {
      id: p.id,
      value: value
    });
  }; //handle the resize and event attaching and detaching


  useEffect(function () {
    var handleResizeStop = function handleResizeStop() {
      setIsResizing(false);
    }; //calculate the height by also enforcing the min/max resize height


    var handleResizeMove = function handleResizeMove(event) {
      if (isResizing) {
        var height = resizableDivRef.current.offsetHeight + (event.clientY - prevY); //calculated the new height of the component
        //checks if we need to enforce a minimum height

        if (!isNaN(p.minResizeHeight)) {
          height = Math.max(p.minResizeHeight, height);
        } //checks if we need to enforce a maximum height


        if (!isNaN(p.maxResizeHeight)) {
          height = Math.min(p.maxResizeHeight, height);
        }

        setComponentHeight(height + "px");
        setPrevY(event.clientY);
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeStop);
    }

    return function () {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeStop);
    };
  }, [isResizing, prevY, p.minResizeHeight, p.maxResizeHeight]);

  var _hasError = evalPredicate(p.hasError) || !!p.errorMessage;

  var _isReadOnly = evalPredicate(p.readOnly);

  var _isDisabled = evalPredicate(p.isDisabled);

  var _size = p.size || (p.isMultiline ? sizes.MEDIUM : sizes.SMALL);

  var _cls = cx('ck', 'rich-text', (_cx = {
    'rich-text--error': _hasError,
    'rich-text--read-only': _isReadOnly,
    'rich-text--disabled': _isDisabled,
    // note that for this component, isDisabled and readOnly are functionally the same thing
    'rich-text--multiline': p.isMultiline,
    'rich-text--fluid': p.isFluid,
    'rich-text--focus': hasFocus,
    'rich-text--has-error-icon': p.hasErrorIcon,
    'rich-text--inverted': p.isInverted
  }, _cx["rich-text--size-" + _size] = _size, _cx));

  var _editor = /*#__PURE__*/React.createElement("div", {
    className: "rich-text__editor-wrapper",
    ref: resizableDivRef,
    style: {
      height: componentHeight,
      maxHeight: componentHeight
    }
  }, /*#__PURE__*/React.createElement(QuillEditor, {
    value: p.value,
    isMultiline: p.isMultiline,
    toolbarOptions: p.toolbarOptions,
    readOnly: _isReadOnly || _isDisabled,
    placeholder: p.placeholder,
    useMentions: p.useMentions,
    mentionList: p.mentionList,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
    bounds: p.bounds
  }), /*#__PURE__*/React.createElement("div", {
    className: "rich-text__help-icon"
  }, /*#__PURE__*/React.createElement(Tooltip, {
    tooltip: "Select some text and use the popup toolbar to apply styling. " + (p.isMultiline ? 'Use the return key to add line breaks.' : ''),
    position: positions.LEFT
  }, /*#__PURE__*/React.createElement(Icon, {
    name: icons.TEXT,
    size: sizes.X5
  }))), p.isResizable ? /*#__PURE__*/React.createElement(ResizeHandle, {
    onMouseDown: handleResizeStart
  }) : null);

  return /*#__PURE__*/React.createElement("div", {
    className: _cls,
    style: p.style
  }, !!p.label ? /*#__PURE__*/React.createElement(PrivateLabel, {
    text: p.label
  }) : null, p.hasErrorIcon ? /*#__PURE__*/React.createElement("div", {
    className: "error-wrapper"
  }, _editor, /*#__PURE__*/React.createElement(ErrorIcon, {
    errorMessage: p.errorMessage,
    errorMessagePosition: p.errorMessagePosition
  })) : /*#__PURE__*/React.createElement("div", {
    className: "error-wrapper"
  }, /*#__PURE__*/React.createElement(Tooltip, {
    tooltip: p.errorMessage,
    color: colors.ERROR,
    position: p.errorMessagePosition
  }, _editor)));
};

RichText.propTypes = {
  label: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.string,
  placeholder: PropTypes.string,
  isFluid: PropTypes.bool,
  isMultiline: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  hasErrorIcon: PropTypes.bool,
  errorMessage: PropTypes.string,
  errorMessagePosition: PropTypes.oneOf(positions.ALL),
  readOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  isDisabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  useMentions: PropTypes.bool,
  mentionList: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    fullText: PropTypes.string
  })),
  toolbarOptions: PropTypes.arrayOf(PropTypes.oneOf(richTextToolbar.ALL)),
  isResizable: PropTypes.bool,
  minResizeHeight: PropTypes.number,
  maxResizeHeight: PropTypes.number,
  size: PropTypes.oneOf([sizes.SMALL, sizes.MEDIUM, sizes.LARGE, sizes.X6, sizes.X7])
};
RichText.defaultProps = {
  label: null,
  id: undefined,
  value: '',
  placeholder: null,
  isFluid: true,
  isMultiline: true,
  hasError: false,
  hasErrorIcon: true,
  errorMessage: undefined,
  errorMessagePosition: positions.TOP_LEFT,
  readOnly: false,
  isDisabled: false,
  onChange: _.noop,
  onBlur: _.noop,
  onFocus: _.noop,
  useMentions: false,
  mentionList: [],
  toolbarOptions: richTextToolbar.ALL,
  isResizable: true,
  minResizeHeight: 35,
  maxResizeHeight: undefined,
  //no max limit
  size: undefined //when not given, will be calculated based on isMultiline

};
export default whitelistStyles()(getContext({
  isInverted: PropTypes.bool
})(RichText));
//# sourceMappingURL=index.js.map